"use client";

import Link from "@/components/Link";
import * as prismicH from "@prismicio/helpers";
import { LinkField } from "@prismicio/types";
import dynamic from "next/dynamic";
import React, { useState } from "react";
import * as Yup from "yup";

import { CookieControlButton } from "@/components/OneTrust";
import SocialsList from "@/components/SocialsList";
import { Container } from "@cgc/ui/layout";
import { Paragraph } from "@cgc/ui/typography";

import { signUpOnOmetria } from "@/app/data/ometria/ometria.data";
import { validationEmail } from "@/helpers/validation-helpers";
import {
  FooterDocument,
  FooterDocumentDataCol1LinksItem,
  FooterDocumentDataCol2LinksItem,
  FooterDocumentDataCol3LinksItem,
  SettingsDocument,
} from "@/prismicio-types";
import { extractSocialsLinksProps } from "@/utils/socials";
import { Button } from "@cgc/ui/button";
import { sendGTMEvent } from "@next/third-parties/google";
import { Form, Formik, FormikHelpers } from "formik";
import { ChevronRight } from "lucide-react";
import TextInputField from "../ui/Form/TextInput";
import PaymentIcon from "./PaymentIcon";

const ReviewsWidget = dynamic(
  () => import("@/components/ReviewsIO/ReviewsWidget"),
  {
    ssr: false,
  },
);

const renderLink = ({ link, label }: { link: LinkField; label: String }) => {
  if (label.toLowerCase() === "cookie settings") {
    return <CookieControlButton>{label}</CookieControlButton>;
  }

  return (
    <Link
      className="uppercase no-underline decoration-1 underline-offset-2 hover:text-black hover:underline"
      href={prismicH.asLink(link) ?? "/"}
    >
      <Paragraph as="span" size="p2" className="text-sm lg:text-sm">
        {label}
      </Paragraph>
    </Link>
  );
};

type LinkColumnProps = {
  title: string;
  links:
    | FooterDocumentDataCol1LinksItem[]
    | FooterDocumentDataCol2LinksItem[]
    | FooterDocumentDataCol3LinksItem[];
};

const LinksColumn: React.FC<LinkColumnProps> = ({ title, links }) => (
  <div className="mb-2 py-2.5">
    <ul className="space-y-2.5">
      <li className="mb-[6px]">
        <Paragraph size="p2" className="text-sm font-bold uppercase lg:text-sm">
          {title}
        </Paragraph>
      </li>
      {links.map(({ link, label }, i) => (
        <li key={`${title}_${link}_${i}`}>
          {renderLink({ link, label: label as string })}
        </li>
      ))}
    </ul>
  </div>
);

type Props = {
  footer: FooterDocument;
  settings: SettingsDocument;
};

interface EmailCapture {
  email: string;
}

const EmailCapture = () => {
  const onSubmit = async (
    values: EmailCapture,
    formikHelpers: FormikHelpers<EmailCapture>,
  ) => {
    try {
      const response = await signUpOnOmetria(values.email);

      if (response.ok) {
        sendGTMEvent({
          event: "email_sign_up",
          context: "newsletter",
          action: "email_sign_up",
        });
        formikHelpers.setSubmitting(false);
      } else {
        setErrors(true);
      }
    } catch (error) {
      setErrors(true);
      throw new Error(
        "Something went wrong with signing up for the newsletter.",
      );
    }
    setSubmitted(true);
  };

  const validationSchema = Yup.object<EmailCapture>({
    email: validationEmail,
  });

  const [submitted, setSubmitted] = useState<boolean>(false);
  const [errors, setErrors] = useState<boolean>(false);

  return (
    <div className="py-2.5">
      <Paragraph size="p2" className="text-sm font-bold uppercase lg:text-sm">
        Sign up
      </Paragraph>
      {submitted && !errors ? (
        <Paragraph size="p1" className="text-sm">
          Thank you for subscribing!
        </Paragraph>
      ) : (
        <Formik<EmailCapture>
          initialValues={{ email: "" }}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
        >
          {({ isValid, isSubmitting }) => (
            <Form>
              <div className="relative w-[300px] md:w-full">
                <label className="text-sm uppercase text-black">
                  Subscribe to our newsletter for exclusive offers, cocktail
                  recipes and more!
                </label>
                <div className="flex flex-row">
                  <TextInputField
                    className={"email_capture"}
                    placeholder="Enter your email"
                    autoComplete="off"
                    name="email"
                  />
                  <Button
                    type="submit"
                    color="unstyled"
                    disabled={!isValid || isSubmitting}
                    isLoading={isValid && isSubmitting}
                  >
                    <ChevronRight />
                  </Button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      )}
    </div>
  );
};

const Footer: React.FC<Props> = ({ footer, settings }) => {
  const { data } = footer;

  return (
    <footer className="bg-cgc-green">
      <Container>
        <div className="mb-14 grid md:grid-cols-5 md:gap-1 lg:gap-4 lg:pt-[30px]">
          <LinksColumn
            title={data.col_1_title as string}
            links={data.col_1_links}
          />
          <LinksColumn
            title={data.col_2_title as string}
            links={data.col_2_links}
          />
          <LinksColumn
            title={data.col_3_title as string}
            links={data.col_3_links}
          />
          <EmailCapture />
          <ReviewsWidget />
        </div>
        <div className="border-t-cgc-white flex flex-col border-t-[1px] py-2.5 lg:flex-row lg:justify-between">
          <div className="mb-5 grid grid-flow-col justify-center gap-4">
            {data.visa && <PaymentIcon src="/visa-logo.svg" alt="Visa" />}
            {data.mastercard && (
              <PaymentIcon src="/mastercard-logo.svg" alt="MasterCard" />
            )}
            {data.amex && <PaymentIcon src="/amex-logo.svg" alt="AMEX" />}
            {data.applepay && (
              <PaymentIcon src="/applepay-logo.svg" alt="Apple Pay" />
            )}
            {data.paypal && <PaymentIcon src="/paypal-logo.svg" alt="PayPal" />}
            {data.klarna && <PaymentIcon src="/klarna-logo.svg" alt="Klarna" />}
          </div>
          <SocialsList {...extractSocialsLinksProps(settings)} />
        </div>
      </Container>
    </footer>
  );
};

export default Footer;
